const EQUALITY_OPS = new Set(["==", "in", "not-in"]);
const RANGE_OPS = new Set(["<", "<=", ">", ">=", "!="]);
const ARRAY_OPS = new Set(["array-contains", "array-contains-any"]);

/**
 * Analyze a query and decide whether a Firestore composite index is required.
 * Mirrors Firestore's rules: single-field queries are auto-indexed; composite
 * indexes are needed once you combine filters/order across multiple fields
 * (or use range/array filters together with an orderBy on another field).
 */
export function analyzeQuery({ collectionName, clauses = [], orderByField = "", orderByDir = "asc" }) {
  const active = clauses.filter((c) => c.field && c.op);

  const equalityFields = [];
  const rangeFields = [];
  const arrayFields = [];

  active.forEach((c) => {
    if (EQUALITY_OPS.has(c.op)) equalityFields.push(c.field);
    else if (RANGE_OPS.has(c.op)) rangeFields.push(c.field);
    else if (ARRAY_OPS.has(c.op)) arrayFields.push(c.field);
  });

  const distinctFields = new Set([...equalityFields, ...rangeFields, ...arrayFields]);
  if (orderByField) distinctFields.add(orderByField);

  const warnings = [];
  const rangeSet = new Set(rangeFields);
  if (rangeSet.size > 1) {
    warnings.push("Firestore allows range/inequality filters on only one field per query.");
  }
  if (orderByField && rangeSet.size && !rangeSet.has(orderByField)) {
    warnings.push(`A range filter exists on a different field than orderBy (${orderByField}); orderBy must match the range field.`);
  }

  // Composite index needed when more than one distinct field participates, or
  // an array filter is combined with any other filter/order.
  const needsComposite =
    distinctFields.size > 1 || (arrayFields.length > 0 && active.length + (orderByField ? 1 : 0) > 1);

  const orderedFields = [];
  const seen = new Set();
  const push = (fieldPath, config) => {
    if (!fieldPath || seen.has(fieldPath)) return;
    seen.add(fieldPath);
    orderedFields.push({ fieldPath, ...config });
  };

  // Firestore index field order: equality filters, then array, then range/orderBy.
  equalityFields.forEach((f) => push(f, { order: "ASCENDING" }));
  arrayFields.forEach((f) => push(f, { arrayConfig: "CONTAINS" }));
  rangeFields.forEach((f) => push(f, { order: orderByField === f && orderByDir === "desc" ? "DESCENDING" : "ASCENDING" }));
  if (orderByField) push(orderByField, { order: orderByDir === "desc" ? "DESCENDING" : "ASCENDING" });

  const index = needsComposite
    ? {
        collectionGroup: collectionName || "COLLECTION_ID",
        queryScope: "COLLECTION",
        fields: orderedFields,
      }
    : null;

  return {
    needsComposite,
    equalityFields,
    rangeFields,
    arrayFields,
    orderByField,
    warnings,
    index,
  };
}

export function buildIndexesJson(index) {
  return {
    indexes: index ? [index] : [],
    fieldOverrides: [],
  };
}

export function indexFieldSummary(field) {
  if (field.arrayConfig) return `${field.fieldPath} (array: ${field.arrayConfig})`;
  return `${field.fieldPath} (${field.order})`;
}
